import React, { lazy, Suspense, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { useAuth } from '../../hooks/use-auth';
import * as videosService from '../../services/api/videos';
import { strings } from '../../localization/strings';
import { fromTimestamp } from '../../services/formatter/date';
import Heading1 from '../../components/common/headings/heading1';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import CommonButtonDefault from '../../components/common/buttons/default';

const VideoPlayer = lazy(() => import('./player'));

export default function VideoViewPageComponent() {
  const { id } = useParams();
  const { token } = useAuth();
  const [shareMessage, setShareMessage] = useState('');
  const validId = !!id && /^[1-9]\d*$/.test(id) && Number.isSafeInteger(Number(id));
  const {
    data: video,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['video', id],
    queryFn: () => videosService.one(token, Number(id)),
    enabled: validId,
    refetchOnWindowFocus: false,
    cacheTime: 0,
    retry: false,
  });
  const notFound = !validId || video === null || (axios.isAxiosError(error) && error.response?.status === 404);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/videos/view/${id}`);
      setShareMessage(strings.videos.linkCopied);
    } catch {
      setShareMessage(strings.videos.copyFailed);
    }
  };

  return (
    <div>
      <Link to="/videos" className="mb-4 inline-block text-sm text-primary">
        {strings.videos.back}
      </Link>
      {validId && isLoading && <CommonAnimatedLoader />}
      {notFound && <AlertPrimary type="danger" text={strings.videos.notFound} />}
      {!notFound && error instanceof Error && <AlertPrimary type="danger" text={error.message} />}
      {video && (
        <>
          <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <Heading1>{video.title}</Heading1>
              <div className="mt-2 text-sm text-gray-500">
                {video.created_at && fromTimestamp(video.created_at, true)}
                {video.category && <span className="ml-3">{video.category.title}</span>}
              </div>
            </div>
            <CommonButtonDefault type="button" secondary onClick={copyLink}>
              {strings.videos.copyLink}
            </CommonButtonDefault>
          </header>
          {shareMessage && (
            <p role="status" className="mb-3 text-sm text-gray-600">
              {shareMessage}
            </p>
          )}
          {video.playlist_url ? (
            <Suspense fallback={<CommonAnimatedLoader />}>
              <VideoPlayer key={video.id} src={video.playlist_url} poster={video.image_url} title={video.title} />
            </Suspense>
          ) : (
            <AlertPrimary text={strings.videos.unavailable} />
          )}
          {video.description && (
            <div className="mb-5 rounded-lg bg-white p-4 sm:p-5 mt-5">
              <p className="whitespace-pre-wrap text-sm text-gray-700">{video.description}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
